import { createRouter, createWebHashHistory } from 'vue-router';
import { message, isMobile } from '🔗/utils/utils';

// import stores from '@/stores';
// import api from '🔗/api';

import { requests } from '🔗/utils/requests';

const handleRouterError = (error) => {
    const t = stores.locale().t();
    const errors = [
        {
            code: ['fetch', 'load', 'dynamically', 'importing'],
            action: () => message.error(t('common.errors.importError'))
        }
    ];

    const errorWords = error.message.toLowerCase().split(' ');
    const action = errors.find((item) => item.code.some((r) => errorWords.indexOf(r) !== -1));

    if (action) {
        return action.action();
    } else {
        message.error(error.message);
    }
};

const checkPermissions = (permissions) => {
    if (!permissions?.length) {
        return true;
    }

    const roles = stores.user().profile?.roles || [];
    
    return permissions.some(item => !!roles.find(role => role.roleId === item) ||
        (item === 'artist' && stores.user().isArtist)
    )
}

const testIsMobile = async (to, from, next) => {
    const page = routes.find((page) => page.name === to.name);

    if (isMobile()) {
        next({ name: 'unsupported' });
    } else if (page) {
        next();
        return
    } else {
        next({ name: 'error-page' });
    }
};


const testOnPermissions = async (to, from, next) => {
    const page = routes.find((page) => page.name === to.name);

    if (isMobile()) {
        next({ name: 'unsupported' });
    } else if (!await stores.user()?.checkHasToken()) {
        next({ name: 'login', query: { redirect: to.fullPath } });
    } else if (page) {
            next();
            return
        if (checkPermissions(page.permission)) {
            next();
        } else {
            next({ name: 'login' });
            // next({ name: 'access-denied' });
        }
    } else {
        next({ name: 'error-page' });
    }
};


const goHome = async (to, from, next) => {
    if (!await stores.user().checkHasToken()) {
        next({ name: 'login' });
        return;
    }

    const roles = stores.user().profile?.roles?.map(item => item.roleId) || [];

    let name;

    stores.nav().menu.forEach(item => {
        if (!name && checkPermissions(item.permission)) {
            name = item.name;
        }
    });

    if (name) {
        next({ name });
    } else {
        next({ name: 'access-denied' })
    }
};

const routes = [
    {
        path: '/',
        name: '/',
        beforeEnter: goHome
    },
    {
        path: '/artists',
        name: 'artists',
        component: () => import('../pages/Artists/Artists.vue'),
        permission: ['artist_curator', 'track_curator', 'artist'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/categories',
        name: 'categories',
        component: () => import('../pages/Dictionaries/Categories.vue'),
        permission: ['dictionary_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/genres',
        name: 'genres',
        component: () => import('../pages/Dictionaries/Genres.vue'),
        permission: ['dictionary_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/instruments',
        name: 'instruments',
        component: () => import('../pages/Dictionaries/Instruments.vue'),
        permission: ['dictionary_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/moods',
        name: 'moods',
        component: () => import('../pages/Dictionaries/Moods.vue'),
        permission: ['dictionary_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/releases',
        name: 'releases',
        component: () => import('../pages/Releases/Releases.vue'),
        permission: ['track_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/playlists',
        name: 'playlists',
        component: () => import('../pages/Playlists/Playlists.vue'),
        permission: ['playlist_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/roles',
        name: 'roles',
        component: () => import('../pages/Roles/Roles.vue'),
        permission: ['admin'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/tags',
        name: 'tags',
        component: () => import('../pages/Dictionaries/Tags.vue'),
        permission: ['dictionary_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/tracks',
        name: 'tracks',
        component: () => import('../pages/Tracks/Tracks.vue'),
        permission: ['track_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/licenses',
        name: 'licenses',
        component: () => import('../pages/Licenses/Licenses.vue'),
        permission: ['track_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/showcase',
        name: 'showcase',
        component: () => import('../pages/Showcase/Showcase.vue'),
        permission: ['showcase_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/users',
        name: 'users',
        component: () => import('../pages/Users/Users.vue'),
        permission: ['admin', 'playlist_curator', 'artist_curator'],
        beforeEnter: testOnPermissions,
        abort: []
    },
    {
        path: '/error',
        name: 'error',
        component: () => import('../pages/Error.vue'),
        abort: []
    },
    {
        path: '/access-denied',
        name: 'access-denied',
        component: () => import('../pages/AccessDenied.vue'),
        abort: []
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('../pages/Login.vue'),
        beforeEnter: testIsMobile,
        abort: []
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'error-page',
        beforeEnter: goHome
    },
    {
        path: '/unsupported',
        name: 'unsupported',
        component: () => import('../pages/Unsupported.vue'),
        abort: []
    }
];

const router = createRouter({
    history: createWebHashHistory(import.meta.env.VITE_APP_BASE_URL),
    routes
});

router.back = function (path) {
    this.options?.history?.state?.back ? this.go(-1) : this.push(path || '/');
};

router.beforeEach((to, from, next) => {
    if (stores.modals().back()) {
        next(false);
        return;
    }

    if (from?.name) {
        routes.forEach((item) => {
            if (item?.name === from?.name && item.abort?.length) {
                item.abort.forEach((code) => {
                    if (code.split(':id').length === 1) {
                        requests[code].abort();
                    } else {
                        requests[code.split(':id')[0]].abort(from.params.id);
                    }
                });
            }
        });
    }

    if (to?.name && stores.nav().page !== to.name) {
        stores.nav().setPage(to.name);
    }
    next();
});

router.onError(handleRouterError);

export default router;
